// ============================================================
// PATCHISTRY CONCIERGE — Sage's on-site chat widget
// Vanilla JS, no framework. Calls /api/concierge/chat on worker,
// renders Sage's replies + product cards, fires /cart/add.js.
// Owner: snippets/patchistry-concierge.liquid
// ============================================================

(function () {
  'use strict';

  const ENDPOINT = 'https://patchistry-api.patchistry.workers.dev/api/concierge/chat';
  const STORAGE_KEY = 'pp_conc_state_v1';
  const AUTO_OPEN_DELAY_MS = 12000; // 12s on page before auto-trigger
  const MAX_HISTORY = 16;

  const root = document.getElementById('pp-concierge');
  if (!root) return;

  // Hide entirely on /checkout + /account — those are post-decision spaces.
  // /cart IS now a Sage surface (she's cart-aware, offers coupon + email-save on hesitation).
  const path = location.pathname;
  if (/^\/(checkouts?|account)/.test(path)) return;

  // ── State ────────────────────────────────────────────────
  const state = loadState();
  let autoOpenTimer = null;
  let isSending = false;
  let isHistoryRender = false;  // true during renderHistory() to suppress animations + chips

  // ── DOM refs ─────────────────────────────────────────────
  const bubble = root.querySelector('.pp-conc-bubble');
  const panel = root.querySelector('.pp-conc-panel');
  const closeBtn = root.querySelector('.pp-conc-close');
  const messagesEl = root.querySelector('.pp-conc-messages');
  const inputEl = root.querySelector('.pp-conc-input textarea');
  const sendBtn = root.querySelector('.pp-conc-send');
  const tooltipEl = root.querySelector('.pp-conc-tooltip');
  const statusEl = document.getElementById('pp-conc-status');
  const chipsEl = document.getElementById('pp-conc-chips');

  // ── Init ─────────────────────────────────────────────────
  bubble.addEventListener('click', open);
  closeBtn.addEventListener('click', close);
  sendBtn.addEventListener('click', () => send(inputEl.value));
  inputEl.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(inputEl.value);
    }
  });
  inputEl.addEventListener('input', autoResize);
  window.addEventListener('patchistry:cart-state', (event) => {
    refreshCartGreeting(event.detail?.cart);
  });

  // Render any existing history. Greeting strategy:
  // - On /cart: greeting is cart-aware (fetches /cart.js, names the build).
  // - Anywhere else: generic occasion-discovery greeting.
  // If saved messages exist but are JUST stale greetings (no real user
  // interaction), wipe and re-greet for current page context.
  const allMessagesAreGreetings = state.messages.length > 0 && state.messages.every((m) => m._sageGreeting);
  if (allMessagesAreGreetings) {
    state.messages = [];
    saveState();
  }
  if (state.messages.length > 0) renderHistory();
  else greetIfFirst();

  // Pinterest visitor detection — auto-open Sage with intent-specific greeting
  // and 3 ready-made starter builds. Per ai-shopping-attribution-cascade:
  // Pinterest visitors are visual-first, high-intent, single-session converters.
  // Don't make them figure out the builder. Hand them the build.
  const fromPinterest = (
    /pinterest\.com/i.test(document.referrer || '') ||
    /[?&]utm_source=pinterest/i.test(location.search || '')
  );

  if (fromPinterest && !state.hasInteracted && !state.hasOpened) {
    setTimeout(autoOpenForPinterest, 3000);
  } else {
    // Peek-out tooltip — introduces Sage so users know what the bubble is.
    // First visit: shows 5s after load for 6s. Second nudge at 90s if still unopened.
    // Suppressed entirely once user has interacted with chat at any point.
    scheduleTooltip();
  }

  function scheduleTooltip() {
    if (state.hasInteracted || state.hasOpened) {
      root.classList.add('has-interacted');
      return;
    }
    if (!tooltipEl) return;
    setTimeout(() => showTooltip(6000), 5000);
    setTimeout(() => {
      if (!state.hasOpened) showTooltip(5000);
    }, 90000);
  }

  function autoOpenForPinterest() {
    if (state.hasInteracted || state.hasOpened) return;
    open();
    // Seed Sage's greeting + 3 starter build cards INSTANTLY — no API call,
    // pre-rendered. User can either click one (instant cart-fill) or chat.
    const greeting = {
      role: 'assistant',
      content: "Hey from Pinterest 👋 saw you came over from a hat pic. Three starter builds people are loving this week — pick one or tell me your occasion.",
      _sageGreeting: true,
      timestamp: Date.now(),
      suggestions: [
        { type: 'canvas', handle: 'the-hat-black', title: 'The Canvas — Black', variantId: '52261121884472', price: '30.00', image: 'https://cdn.shopify.com/s/files/1/0745/4041/7336/files/Black_7242aeda-2c5c-4369-9179-7e59ce7e5291.png' }
      ],
      addAllLabel: null,
      _pinterestStarters: [
        {
          name: 'Nashville Bach',
          subtitle: 'Black Canvas + 6 patches, about $85',
          variantIds: ['52261121884472', '52258523316536', '52296479211832', '52255955124536', '52386234564920', '52386234335544', '52296620638520'],
          url: '/pages/nashville-bachelorette-hat',
        },
        {
          name: 'Wedding Party',
          subtitle: 'Black Canvas + 6 patches, about $90',
          variantIds: ['52261121884472', '52296444838200', '52296457027896', '52296497702904', '52296464859448', '52258440577336', '52258489303352'],
          url: '/pages/wedding-hat-build',
        },
        {
          name: 'Sorority Bid Day',
          subtitle: 'Black Canvas + 6 patches, about $80',
          variantIds: ['52261121884472', '52296444838200', '52296562311480', '52296568734008', '52296525119800', '52386234597688', '52386234171704'],
          url: '/pages/sororities',
        },
      ],
    };
    // Replace any default greeting from greetIfFirst() — Pinterest takes priority
    state.messages = [greeting];
    saveState();
    renderMessage(greeting);
    renderPinterestStarters(greeting._pinterestStarters);
  }

  function renderPinterestStarters(starters) {
    const wrap = document.createElement('div');
    wrap.className = 'pp-conc-starters';
    starters.forEach((s) => {
      const card = document.createElement('div');
      card.className = 'pp-conc-starter';
      card.innerHTML = `
        <div class="pp-conc-starter-name">${escapeHtml(s.name)}</div>
        <div class="pp-conc-starter-sub">${escapeHtml(s.subtitle)}</div>
        <div class="pp-conc-starter-actions">
          <button type="button" class="pp-conc-starter-add">🎯 Build this</button>
          <a href="${escapeAttr(s.url)}" class="pp-conc-starter-link">See guide</a>
        </div>
      `;
      card.querySelector('.pp-conc-starter-add').addEventListener('click', (e) => {
        addStarterToCart(s, e.currentTarget);
      });
      wrap.appendChild(card);
    });
    messagesEl.appendChild(wrap);
    scrollToBottom();
  }

  async function addStarterToCart(starter, btn) {
    btn.disabled = true;
    const original = btn.textContent;
    btn.textContent = 'Adding…';
    try {
      const items = starter.variantIds.map((id) => ({ id: parseInt(id, 10), quantity: 1 }));
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error('cart add failed');
      btn.textContent = `✓ Added — go to cart`;
      bumpCartCount();
      window.dispatchEvent(new CustomEvent('patchistry:cart-updated', { detail: { source: 'concierge-pinterest', items } }));
      setTimeout(() => { window.location.href = '/cart'; }, 1100);
    } catch (e) {
      btn.textContent = 'Try again';
      btn.disabled = false;
    }
  }

  function showTooltip(durationMs) {
    if (!tooltipEl || state.hasOpened || state.hasInteracted) return;
    tooltipEl.classList.add('is-visible');
    setTimeout(() => tooltipEl.classList.remove('is-visible'), durationMs);
  }

  // ── Functions ────────────────────────────────────────────

  function loadState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return defaultState();
      const parsed = JSON.parse(raw);
      return { ...defaultState(), ...parsed };
    } catch (e) {
      return defaultState();
    }
  }

  function defaultState() {
    return { messages: [], hasOpened: false, hasInteracted: false, conversationId: null, chipsShown: false };
  }

  function saveState() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) { /* quota or private mode — ignore */ }
  }

  function open() {
    root.classList.add('is-open', 'has-interacted');
    state.hasOpened = true;
    state.hasInteracted = true;
    saveState();
    if (tooltipEl) tooltipEl.classList.remove('is-visible');
    document.body.style.overflow = 'hidden';
    setTimeout(() => inputEl.focus(), 320);
  }

  function close() {
    root.classList.remove('is-open');
    // Restore overflow if header drawer isn't also open
    const headerDrawer = document.getElementById('p-nav-drawer');
    if (!headerDrawer || !headerDrawer.classList.contains('is-open')) {
      document.body.style.overflow = '';
    }
  }

  function autoResize() {
    inputEl.style.height = 'auto';
    inputEl.style.height = Math.min(inputEl.scrollHeight, 100) + 'px';
  }

  async function greetIfFirst() {
    const onCart = /^\/cart/.test(location.pathname);
    let greetingText;
    if (onCart) {
      // Cart-aware greeting — fetch cart and reference the build by name
      const cart = await fetchCart();
      greetingText = cartGreeting(cart);
    } else {
      greetingText = "Welcome to Patchistry! 🚀 Tell me the vibe or occasion you're designing for (bach party, golf, adventure, dad gift, etc.) and I'll lay out a custom starter build for you right now.";
    }
    const greeting = {
      role: 'assistant',
      content: greetingText,
      _sageGreeting: true,
      timestamp: Date.now(),
    };
    state.messages.push(greeting);
    saveState();
    renderMessage(greeting);
    // Show quick reply chips on non-cart first greeting
    if (!onCart && !state.chipsShown) renderQuickReplies();
  }

  function cartGreeting(cart) {
    if (
      window.PatchistryCartState &&
      typeof window.PatchistryCartState.greetingForCart === 'function'
    ) {
      return window.PatchistryCartState.greetingForCart(cart);
    }

    if (cart && cart.item_count > 0) {
      const items = cart.items || [];
      const topItems = items.slice(0, 4).map((item) => (item.product_title || item.title || '').split(' | ')[0]).filter(Boolean).join(', ');
      const moreCount = items.length > 4 ? ` (+${items.length - 4} more)` : '';
      const total = (cart.total_price / 100).toFixed(2);
      return `Awesome setup! I see you've got ${topItems}${moreCount} in your build ($${total}). Want to finish your layout now, or should I email this configuration to you so you don't lose it?`;
    }

    return "Ready to build? Tell me the occasion (bach party, golf day, outdoor adventure, or custom gift) and let's put a layout together.";
  }

  function refreshCartGreeting(cart) {
    if (!/^\/cart/.test(location.pathname) || !cart) return;
    if (!state.messages.length || !state.messages.every((message) => message._sageGreeting)) return;

    const content = cartGreeting(cart);
    if (!content || state.messages[0]?.content === content) return;

    state.messages = [{
      role: 'assistant',
      content,
      _sageGreeting: true,
      timestamp: Date.now(),
    }];
    saveState();
    renderHistory();
  }

  function renderHistory() {
    messagesEl.innerHTML = '';
    isHistoryRender = true;
    state.messages.forEach((m) => renderMessage(m));
    isHistoryRender = false;
  }

  function renderMessage(m) {
    const isUser = m.role === 'user';
    const isFresh = !isHistoryRender;

    // Check if we should render a timestamp separator
    if (m.timestamp) renderTimestamp(m.timestamp);

    const msgEl = document.createElement('div');
    msgEl.className = 'pp-conc-msg ' + (isUser ? 'pp-conc-msg-user' : 'pp-conc-msg-sage');
    msgEl.textContent = m.content;

    // Wrap Sage messages in a row with avatar
    let rowEl = null;
    if (!isUser) {
      rowEl = document.createElement('div');
      rowEl.className = 'pp-conc-msg-row';
      if (isFresh) rowEl.classList.add('is-new');
      const avatar = document.createElement('div');
      avatar.className = 'pp-conc-msg-avatar';
      avatar.setAttribute('aria-hidden', 'true');
      avatar.textContent = '✨';
      rowEl.appendChild(avatar);
      rowEl.appendChild(msgEl);
      messagesEl.appendChild(rowEl);
    } else {
      if (isFresh) msgEl.classList.add('is-new');
      messagesEl.appendChild(msgEl);
    }

    // Render product suggestions + extras if Sage's message has them
    if (!isUser && m.suggestions && m.suggestions.length > 0) {
      const cardsEl = document.createElement('div');
      cardsEl.className = 'pp-conc-cards';
      m.suggestions.forEach((s) => {
        cardsEl.appendChild(renderCard(s));
      });
      messagesEl.appendChild(cardsEl);
    }

    if (!isUser && m.addAllLabel && m.suggestions?.length) {
      const addAllBtn = document.createElement('button');
      addAllBtn.className = 'pp-conc-addall';
      addAllBtn.textContent = m.addAllLabel;
      addAllBtn.addEventListener('click', () => addAllToCart(m.suggestions, addAllBtn));
      messagesEl.appendChild(addAllBtn);
    }

    if (!isUser && m.pageRecommendation) {
      const link = document.createElement('a');
      link.className = 'pp-conc-pagerec';
      link.href = m.pageRecommendation.url;
      link.innerHTML = `See: ${escapeHtml(m.pageRecommendation.title)}`;
      messagesEl.appendChild(link);
    }

    // Coupon offer — clickable button that auto-applies the discount
    if (!isUser && m.couponOffer && m.couponApplyUrl) {
      const couponBtn = document.createElement('a');
      couponBtn.className = 'pp-conc-coupon';
      couponBtn.href = m.couponApplyUrl;
      couponBtn.innerHTML = `🎟 Apply <strong>${escapeHtml(m.couponOffer)}</strong> + go to cart`;
      messagesEl.appendChild(couponBtn);
    }

    // Email capture offer — small inline form to save build via email
    if (!isUser && m.emailCaptureOffer) {
      messagesEl.appendChild(renderEmailForm());
    }

    scrollToBottom();
  }

  // ── Timestamp rendering ────────────────────────────────────

  function renderTimestamp(ts) {
    // Only show timestamp if there's a gap (>2 min) from the previous message
    const prevMessages = messagesEl.querySelectorAll('.pp-conc-msg, .pp-conc-msg-row');
    if (prevMessages.length === 0) return; // first message, no gap yet

    // Find the last rendered message's timestamp from state
    const stateIndex = state.messages.findIndex((m) => m.timestamp === ts);
    if (stateIndex <= 0) return;
    const prevTs = state.messages[stateIndex - 1].timestamp;
    if (!prevTs || (ts - prevTs) < 120000) return; // <2 min gap, skip

    const el = document.createElement('div');
    el.className = 'pp-conc-timestamp';
    el.textContent = formatRelativeTime(ts);
    messagesEl.appendChild(el);
  }

  function formatRelativeTime(ts) {
    const diff = Date.now() - ts;
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return 'just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return min + 'm ago';
    const hr = Math.floor(min / 60);
    if (hr < 24) return hr + 'h ago';
    const d = Math.floor(hr / 24);
    return d + 'd ago';
  }

  // ── Quick reply chips ──────────────────────────────────────

  function renderQuickReplies() {
    if (!chipsEl) return;
    const chips = [
      'Bach party 🎉',
      'Dad gift 🎁',
      'Wedding 💍',
      'Just browsing 👀',
    ];
    chipsEl.innerHTML = '';
    chips.forEach((label) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pp-conc-chip';
      btn.textContent = label;
      btn.addEventListener('click', () => {
        clearChips();
        send(label);
      });
      chipsEl.appendChild(btn);
    });
    state.chipsShown = true;
    saveState();
  }

  function clearChips() {
    if (chipsEl) chipsEl.innerHTML = '';
  }

  // ── Header status helper ───────────────────────────────────

  function setStatus(text) {
    if (statusEl) statusEl.textContent = text;
  }

  function renderEmailForm() {
    const form = document.createElement('form');
    form.className = 'pp-conc-emailform';
    form.innerHTML = `
      <input type="email" placeholder="your@email.com" required autocomplete="email">
      <button type="submit">Save build</button>
    `;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const input = form.querySelector('input');
      const btn = form.querySelector('button');
      const email = input.value.trim();
      if (!email) return;
      btn.disabled = true; btn.textContent = 'Saving…';
      // Send the email as Sage's user message so she confirms naturally + the
      // backend triggers Klaviyo via emailCapturedFromUser path.
      await send(email);
      form.remove();
    });
    return form;
  }

  function renderCard(s) {
    const card = document.createElement('div');
    card.className = 'pp-conc-card';
    card.innerHTML = `
      ${s.image ? `<img class="pp-conc-card-img" src="${escapeAttr(s.image)}" alt="${escapeAttr(s.title)}" loading="lazy">` : '<div class="pp-conc-card-img"></div>'}
      <div class="pp-conc-card-meta">
        <div class="pp-conc-card-title">${escapeHtml(s.title)}</div>
        <div class="pp-conc-card-price">$${escapeHtml(s.price)}</div>
        <button class="pp-conc-card-add" data-variant="${escapeAttr(s.variantId)}">Add</button>
      </div>
    `;
    card.querySelector('.pp-conc-card-add').addEventListener('click', (e) => {
      addOneToCart(s.variantId, e.currentTarget);
    });
    return card;
  }

  function showTyping() {
    const rowEl = document.createElement('div');
    rowEl.className = 'pp-conc-msg-row';
    rowEl.id = 'pp-conc-typing';
    const avatar = document.createElement('div');
    avatar.className = 'pp-conc-msg-avatar';
    avatar.setAttribute('aria-hidden', 'true');
    avatar.textContent = '✨';
    const el = document.createElement('div');
    el.className = 'pp-conc-msg-typing';
    el.innerHTML = '<span></span><span></span><span></span>';
    rowEl.appendChild(avatar);
    rowEl.appendChild(el);
    messagesEl.appendChild(rowEl);
    scrollToBottom();
  }

  function hideTyping() {
    const el = document.getElementById('pp-conc-typing');
    if (el) el.remove();
  }

  function scrollToBottom() {
    requestAnimationFrame(() => {
      messagesEl.scrollTop = messagesEl.scrollHeight;
    });
  }

  async function send(text) {
    text = (text || '').trim();
    if (!text || isSending) return;
    isSending = true;
    sendBtn.disabled = true;

    // Append user message to UI + state
    const userMsg = { role: 'user', content: text, timestamp: Date.now() };
    state.messages.push(userMsg);
    renderMessage(userMsg);
    inputEl.value = '';
    autoResize();
    clearChips();

    // Trim to last N messages for prompt economy
    const historyForApi = state.messages
      .filter((m) => !m._sageGreeting) // drop the greeting from API context
      .slice(-MAX_HISTORY)
      .map((m) => ({ role: m.role, content: m.content }));

    // Pull current cart context so Sage knows what's in the cart NOW
    const cart = await fetchCart();

    showTyping();
    setStatus('Sage is typing…');
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: historyForApi,
          page: location.pathname,
          cart: cart,
        }),
      });
      const data = await res.json();
      hideTyping();
      // 'Active now' is a staffing claim nobody can back at 2am. Empty is safe:
      // statusEl is a text node the send path writes to and nothing reads back.
      setStatus('');

      if (data.error) {
        renderMessage({ role: 'assistant', content: "Sorry — I hiccupped. Try again?", timestamp: Date.now() });
      } else {
        const sageMsg = {
          role: 'assistant',
          content: data.reply || '',
          suggestions: data.suggestions || [],
          pageRecommendation: data.pageRecommendation || null,
          addAllLabel: data.addAllLabel || null,
          couponOffer: data.couponOffer || null,
          couponApplyUrl: data.couponApplyUrl || null,
          emailCaptureOffer: data.emailCaptureOffer || false,
          timestamp: Date.now(),
        };
        state.messages.push(sageMsg);
        renderMessage(sageMsg);
      }
      saveState();
    } catch (e) {
      hideTyping();
      setStatus('');
      renderMessage({ role: 'assistant', content: "Connection blip. Mind trying again?", timestamp: Date.now() });
    } finally {
      isSending = false;
      sendBtn.disabled = false;
      inputEl.focus();
    }
  }

  async function fetchCart() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  async function addOneToCart(variantId, btn) {
    btn.disabled = true;
    const original = btn.textContent;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
      });
      if (!res.ok) throw new Error('cart add failed');
      btn.classList.add('is-added');
      btn.textContent = '✓ Added';
      bumpCartCount();
      setTimeout(() => {
        btn.classList.remove('is-added');
        btn.textContent = original;
        btn.disabled = false;
      }, 1800);
    } catch (e) {
      btn.textContent = 'Try again';
      btn.disabled = false;
    }
  }

  async function addAllToCart(suggestions, btn) {
    btn.disabled = true;
    const original = btn.textContent;
    btn.textContent = 'Adding…';
    try {
      const items = suggestions.map((s) => ({ id: parseInt(s.variantId, 10), quantity: 1 }));
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error('cart add failed');
      btn.classList.add('is-added');
      btn.textContent = `✓ Added ${items.length} items`;
      bumpCartCount();
      // Optionally open cart drawer — dispatch event that themes/snippets can listen for
      window.dispatchEvent(new CustomEvent('patchistry:cart-updated', { detail: { source: 'concierge', items } }));
      setTimeout(() => {
        btn.classList.remove('is-added');
        btn.textContent = original;
        btn.disabled = false;
      }, 2200);
    } catch (e) {
      btn.textContent = 'Try again';
      btn.disabled = false;
    }
  }

  async function bumpCartCount() {
    // Update Shopify cart count if header has one (most themes do)
    try {
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      // Update common cart count selectors
      document.querySelectorAll('[data-cart-count], .cart-count, .header__cart-count, #CartCount').forEach((el) => {
        el.textContent = cart.item_count;
      });
      window.dispatchEvent(new CustomEvent('patchistry:cart-count', { detail: cart }));
    } catch (e) { /* not fatal */ }
  }

  // ── Utilities ────────────────────────────────────────────

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[c]);
  }

  function escapeAttr(s) { return escapeHtml(s); }

})();
